import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

export interface JourneyContext {
  msisdn: string;
  micrositeToken: string;
  correlationId?: string;
  source?: 'momo-webview' | 'url';
}

@Injectable({
  providedIn: 'root'
})
export class PwaHandshakeService {

  private heartbeatId?: number;

  waitForJourney(): Observable<JourneyContext> {

    return new Observable(observer => {
      const emitContext = (context: JourneyContext) => {
        observer.next(context);
        observer.complete();
      };

      const urlContext = this.getJourneyContextFromUrl();
      if (urlContext) {
        emitContext(urlContext);
        return undefined;
      }

      if ((environment as any).h5MiniApp) {
        observer.error(
          new Error(
            'H5 mini-app mode requires mobileAccessToken in the launch URL (e.g. ?mobileAccessToken=<token>).'
          )
        );
        return undefined;
      }

      const listener = (event: any) => {

        if (event.type !== 'MoMoWebViewEvent') {
          return;
        }

        const { event: journeyEvent, msisdn } =
          event.detail;

        if (journeyEvent === 'START_JOURNEY') {

          observer.next({
            msisdn,
            micrositeToken: (window as any).micrositeToken,
            source: 'momo-webview'
          });

          observer.complete();
        }
      };

      window.addEventListener(
        'MoMoWebViewEvent',
        listener as EventListener
      );

      return () => {
        window.removeEventListener(
          'MoMoWebViewEvent',
          listener as EventListener
        );
      };
    });
  }

  private getJourneyContextFromUrl(): JourneyContext | null {
    const params = this.getLaunchParams();
    const micrositeToken =
      params.get('mobileAccessToken') ||
      params.get('micrositeToken') ||
      params.get('token') ||
      (window as any).mobileAccessToken ||
      (window as any).micrositeToken ||
      localStorage.getItem('superapp-token') ||
      (environment as any).defaultMobileAccessToken;

    if (!micrositeToken) {
      return null;
    }

    const correlationId =
      params.get('correlationId') ||
      localStorage.getItem('correlation-id') ||
      (environment as any).defaultCorrelationId ||
      crypto.randomUUID();

    return {
      msisdn: params.get('msisdn') || '',
      micrositeToken,
      correlationId,
      source: 'url'
    };
  }

  private getLaunchParams(): URLSearchParams {
    const params = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split('?')[1];

    if (hashQuery) {
      const hashParams = new URLSearchParams(hashQuery);
      hashParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value);
        }
      });
    }

    return params;
  }

  startHeartbeat(): void {

    if ((environment as any).h5MiniApp) {
      return;
    }

    this.stopHeartbeat();

    this.heartbeatId = window.setInterval(() => {

      (window as any).ReactNativeWebView
        ?.postMessage(
          JSON.stringify({
            event: 'IS_STILL_ACTIVE',
            micrositeToken:
              (window as any).micrositeToken
          })
        );

    }, 50000);
  }

  stopHeartbeat(): void {
    if (this.heartbeatId) {
      clearInterval(this.heartbeatId);
    }
  }

  awaitingApproval(transactionId: string): void {

    if ((environment as any).h5MiniApp) {
      return;
    }

    (window as any).ReactNativeWebView
      ?.postMessage(
        JSON.stringify({
          event: 'AWAITING_FOR_APPROVAL',
          transactionId,
          micrositeToken:
            (window as any).micrositeToken
        })
      );
  }

  approved(transactionId: string): void {

    if ((environment as any).h5MiniApp) {
      return;
    }

    (window as any).ReactNativeWebView
      ?.postMessage(
        JSON.stringify({
          event: 'APPROVED',
          transactionId,
          micrositeToken:
            (window as any).micrositeToken
        })
      );
  }

  rejected(transactionId: string): void {

    if ((environment as any).h5MiniApp) {
      return;
    }

    (window as any).ReactNativeWebView
      ?.postMessage(
        JSON.stringify({
          event: 'REJECTED',
          transactionId,
          micrositeToken:
            (window as any).micrositeToken
        })
      );
  }

  done(): void {

    this.stopHeartbeat();

    if ((environment as any).h5MiniApp) {
      return;
    }

    (window as any).ReactNativeWebView
      ?.postMessage(
        JSON.stringify({
          event: 'DONE',
          micrositeToken:
            (window as any).micrositeToken
        })
      );
  }

  error(message: string): void {

    if ((environment as any).h5MiniApp) {
      return;
    }

    (window as any).ReactNativeWebView
      ?.postMessage(
        JSON.stringify({
          event: 'ERROR',
          message,
          micrositeToken:
            (window as any).micrositeToken
        })
      );
  }
}
